import { useEffect, useState } from "react";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import { getAllTopics, getAllLocations } from "@/api/content";
import { getBrandConfig } from "@/config/brand";
import type { Topic, Location } from "@/types/content";

export default function ExploreMore() {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const config = getBrandConfig().exploreMore;

  useEffect(() => {
    Promise.all([getAllTopics(), getAllLocations()]).then(([t, l]) => {
      setTopics(t);
      setLocations(l);
      setLoading(false);
    });
  }, []);

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-8 md:mb-10">
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-compass-3-line text-primary text-xl" />
            </div>
            {config.title}
          </h2>
          <p className="text-sm text-slate-400">
            {config.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {/* Locations Grid */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 mb-4 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-xs" />
              </div>
              {config.locationsLabel}
            </h3>
            {loading ? (
              <SectionSkeleton type="pills" count={8} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {locations.map((loc) => (
                  <a
                    key={loc.id}
                    href={`/locations/${loc.slug}`}
                    className="px-3 py-2 bg-slate-50 hover:bg-primary/5 border border-slate-100 hover:border-primary/20 rounded-lg text-sm text-slate-600 hover:text-primary transition-colors text-center"
                  >
                    {loc.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Topics Grid */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 mb-4 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-hashtag text-xs" />
              </div>
              {config.topicsLabel}
            </h3>
            {loading ? (
              <SectionSkeleton type="pills" count={8} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {topics.map((topic) => (
                  <a
                    key={topic.id}
                    href={`/topics/${topic.slug}`}
                    className="px-3 py-2 bg-slate-50 hover:bg-primary/5 border border-slate-100 hover:border-primary/20 rounded-lg text-sm text-slate-600 hover:text-primary transition-colors text-center"
                  >
                    {topic.title}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Activities / Hot Categories */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 mb-4 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-fire-line text-xs" />
              </div>
              {config.activitiesLabel}
            </h3>
            <div className="flex flex-wrap gap-2">
              {config.activities.map((act) => (
                <a
                  key={act.label}
                  href={act.href}
                  className="px-3.5 py-1.5 bg-slate-50 hover:bg-primary/5 border border-slate-100 hover:border-primary/20 rounded-full text-sm text-slate-600 hover:text-primary transition-colors whitespace-nowrap"
                >
                  {act.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Navigation Links */}
        <div className="mt-10 pt-8 border-t border-slate-100">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "探店精選", href: "/stores", icon: "ri-store-2-line" },
              { label: "文章總覽", href: "/articles", icon: "ri-article-line" },
              { label: "作者專區", href: "/authors", icon: "ri-user-star-line" },
              { label: "加入我們", href: "/join-us", icon: "ri-user-add-line" },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="flex items-center gap-2 px-4 py-3 bg-slate-50 hover:bg-primary/5 border border-slate-100 hover:border-primary/20 rounded-xl text-sm font-medium text-slate-600 hover:text-primary transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={item.icon} />
                </div>
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}